// Lightweight i18n for the embed widget. No framework: a flat key→string
// catalog per locale, a tiny interpolator, and a Preact context so components
// can read the active locale. Locale comes from data-cleanups-locale (default
// "de", no auto-detect). Mirrors the term choices of the Next.js catalog so the
// embed and the platform read the same.

import { createContext } from "preact";
import { useContext } from "preact/hooks";

export type Locale = "de" | "en";
type Params = Record<string, string | number>;

const de: Record<string, string> = {
  viewAria: "Ansicht",
  viewList: "Listenansicht",
  viewTile: "Kachelansicht",
  sectionUpcoming: "Kommende Aktionen",
  sectionPast: "Vergangene Aktionen",
  loading: "Lade…",
  loadError: "Aktionen konnten nicht geladen werden.",
  emptyUpcoming: "Keine kommenden Aktionen.",
  emptyPast: "Keine vergangenen Aktionen.",
  loadMore: "Mehr laden",
  detailsOpen: "{title} — Details öffnen",
  clock: "Uhr",
  statusCancelled: "Abgesagt",
  statusCompleted: "Stattgefunden",
  close: "Schließen",
  detailError: "Aktion konnte nicht geladen werden.",
  titleFallback: "Aktion",
  provided: "Wir stellen",
  bringAlong: "Bitte mitbringen",
  weather: "Bei Regen",
  linkCopied: "In die Zwischenablage kopiert",
  addr: "📍 Adresse",
  meetingPoint: "🎯 Treffpunkt",
  about: "ⓘ Über die Aktion",
  whatToExpect: "Was erwartet euch",
  accessibility: "Barrierefreiheit",
  wasteDisposal: "Müllentsorgung",
  questions: "Fragen?",
  report: "Bericht",
  noReport: "Diese Aktion ist abgeschlossen, kein Bericht eingetragen.",
  exoticFind: "Kurioser Fund",
  calendar: "In den Kalender",
  icsTitle: "Einmalige Kopie – Absagen oder Änderungen werden nicht synchronisiert. Für automatische Updates den Kalender von {org} abonnieren.",
  announcement: "Ankündigung",
  photos: "Fotos",
  showPhoto: "Foto anzeigen",
  prevPhoto: "Vorheriges Foto",
  nextPhoto: "Nächstes Foto",
  prev: "← Vorherige",
  next: "Nächste →",
  prevAction: "Vorherige Aktion",
  nextAction: "Nächste Aktion",
  mapsApp: "📍 In Karten-App",
  share: "Teilen",
  contactForEvent: "Kontakt für dieses Event",
  call: "Anrufen",
  email: "E-Mail",
  followInitiative: "Initiative folgen",
  hostedBy: "Veranstaltet von",
  jointlyWith: "gemeinsam mit",
  coOrgMore: "{count} weitere",
  statGroupEffort: "Engagement",
  statGroupCleanedUp: "Aufgeräumt",
  stat_participants: "Teilnehmende",
  stat_children: "davon Kind|davon Kinder",
  stat_hours: "Einsatzstunden",
  stat_bags: "Müllsack|Müllsäcke",
  stat_kg: "kg Müll",
  stat_cigarettes: "Kippen",
  stat_bottleCaps: "Kronkorken",
  stat_cups: "Einwegbecher",
  stat_bottles: "Flasche|Flaschen",
  stat_treePits: "Baumscheibe|Baumscheiben",
  stat_bulkyWaste: "gemeldeter Sperrmüllfall|gemeldete Sperrmüllfälle",
  item_grabber: "Greifzange",
  item_bags_120l: "Müllsäcke",
  item_gloves: "Handschuhe",
  item_vest: "Warnweste",
  item_bucket: "Eimer",
  item_water_bottle: "Wasserflasche",
  item_sturdy_shoes: "Festes Schuhwerk",
  item_sunscreen: "Sonnenschutz",
  partLabel: "Bist du dabei?",
  partYes: "Ich nehme teil",
  partMaybe: "Vielleicht",
  partWithdraw: "Erneutes Klicken zieht deine Teilnahme zurück.",
  partNameSaved: "Dein Name „{name}“ ist für den Veranstalter sichtbar.",
  partNameRemove: "Name entfernen",
  partNameOptional: "Optional: Dein Name für den Veranstalter",
  partNamePlaceholder: "Vorname",
  partNameSubmit: "Name übermitteln",
  partConsent: "Mein Name wird dem Veranstalter dieser Aktion angezeigt. Ich bin mindestens 16 Jahre alt.",
  partError: "Konnte nicht gespeichert werden. Bitte versuch es später erneut.",
  filterByDistrict: "Nach Bezirk oder Umland filtern",
  filterBySubdistrict: "Nach Ortsteil filtern",
  filterByMunicipality: "Nach Gemeinde filtern",
  filterAll: "Alle",
  footerPrefix: "Alle Aktionen in Berlin auf ",
  footerPrefixDirectory: "Alle Initiativen in Berlin auf ",
  footerMore: "Alle Aktionen in Berlin ansehen",
  footerMoreDirectory: "Alle Initiativen in Berlin ansehen",
  footerProvidedBy: "Bereitgestellt von ",
  dirEvHeading: "Kommende Termine",
  dirMore: "Über die Initiative",
  dirProfile: "Zum vollständigen Profil",
  dirWho: "Wer wir sind",
  dirWhen: "Wann wir aktiv sind",
  dirWhere: "Wo wir aktiv sind",
  dirEmpty: "Keine Initiativen gefunden.",
  dirProfileAria: "{name} — Profil öffnen",
  cityWide: "Berlinweit",
};

const en: Record<string, string> = {
  viewAria: "View",
  viewList: "List view",
  viewTile: "Tile view",
  sectionUpcoming: "Upcoming actions",
  sectionPast: "Past actions",
  loading: "Loading…",
  loadError: "Actions could not be loaded.",
  emptyUpcoming: "No upcoming actions.",
  emptyPast: "No past actions.",
  loadMore: "Load more",
  detailsOpen: "{title} — open details",
  clock: "",
  statusCancelled: "Cancelled",
  statusCompleted: "Took place",
  close: "Close",
  detailError: "Action could not be loaded.",
  titleFallback: "Action",
  provided: "We provide",
  bringAlong: "Please bring",
  weather: "If it rains",
  linkCopied: "Copied to clipboard",
  addr: "📍 Address",
  meetingPoint: "🎯 Meeting point",
  about: "ⓘ About the action",
  whatToExpect: "What to expect",
  accessibility: "Accessibility",
  wasteDisposal: "Waste disposal",
  questions: "Questions?",
  report: "Report",
  noReport: "This action is finished, no report entered.",
  exoticFind: "Weirdest find",
  calendar: "Add to calendar",
  icsTitle: "One-time copy — cancellations or changes are not synced. For automatic updates, subscribe to {org}'s calendar.",
  announcement: "Announcement",
  photos: "Photos",
  showPhoto: "Show photo",
  prevPhoto: "Previous photo",
  nextPhoto: "Next photo",
  prev: "← Previous",
  next: "Next →",
  prevAction: "Previous action",
  nextAction: "Next action",
  mapsApp: "📍 Open in maps",
  share: "Share",
  contactForEvent: "Contact for this event",
  call: "Call",
  email: "Email",
  followInitiative: "Follow initiative",
  hostedBy: "Hosted by",
  jointlyWith: "together with",
  coOrgMore: "{count} more",
  statGroupEffort: "Effort",
  statGroupCleanedUp: "Cleaned up",
  stat_participants: "participants",
  stat_children: "child among them|children among them",
  stat_hours: "hours volunteered",
  stat_bags: "bin bag|bin bags",
  stat_kg: "kg of litter",
  stat_cigarettes: "cigarette butts",
  stat_bottleCaps: "bottle caps",
  stat_cups: "single-use cups",
  stat_bottles: "bottle|bottles",
  stat_treePits: "tree pit|tree pits",
  stat_bulkyWaste: "bulky waste report|bulky waste reports",
  item_grabber: "Grabber",
  item_bags_120l: "Bin bags",
  item_gloves: "Gloves",
  item_vest: "Hi-vis vest",
  item_bucket: "Bucket",
  item_water_bottle: "Water bottle",
  item_sturdy_shoes: "Sturdy shoes",
  item_sunscreen: "Sunscreen",
  partLabel: "Are you in?",
  partYes: "I'm taking part",
  partMaybe: "Maybe",
  partWithdraw: "Clicking again withdraws your participation.",
  partNameSaved: "Your name “{name}” is visible to the organiser.",
  partNameRemove: "Remove name",
  partNameOptional: "Optional: your name for the organiser",
  partNamePlaceholder: "First name",
  partNameSubmit: "Submit name",
  partConsent: "My name is shown to the organiser of this action. I am at least 16 years old.",
  partError: "Could not be saved. Please try again later.",
  filterByDistrict: "Filter by district or surrounding area",
  filterBySubdistrict: "Filter by subdistrict",
  filterByMunicipality: "Filter by municipality",
  filterAll: "All",
  footerPrefix: "All actions in Berlin on ",
  footerPrefixDirectory: "All initiatives in Berlin on ",
  footerMore: "See all actions in Berlin",
  footerMoreDirectory: "See all initiatives in Berlin",
  footerProvidedBy: "Provided by ",
  dirEvHeading: "Upcoming dates",
  dirMore: "About the initiative",
  dirProfile: "See full profile",
  dirWho: "Who we are",
  dirWhen: "When we're active",
  dirWhere: "Where we're active",
  dirEmpty: "No initiatives found.",
  dirProfileAria: "{name} — open profile",
  cityWide: "City-wide",
};

const DICTS: Record<Locale, Record<string, string>> = { de, en };

export function translate(locale: Locale, key: string, params?: Params): string {
  const dict = DICTS[locale] ?? de;
  let s = dict[key] ?? de[key] ?? key;
  if (params) {
    for (const k in params) s = s.split(`{${k}}`).join(String(params[k]));
  }
  return s;
}

export type Translator = (key: string, params?: Params) => string;

export const LocaleContext = createContext<Locale>("de");

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

export function useT(): Translator {
  const locale = useContext(LocaleContext);
  return (key, params) => translate(locale, key, params);
}
